import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { DonorSemen } from '../../models/DonorSemen';
import { DonorSemenFile } from '../../models/DonorSemenFile';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';

const MypageCheckupSemen = () => {
  const { get, post, loading } = useApi();
  const navigate = useNavigate();

  const [semen, setSemen] = useState<DonorSemen | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [delFileIds, setDelFileIds] = useState<number[]>([]);

  useTitle('Ton Bébé | 精液検査結果');

  const fetchSemenData = useCallback(() => {
    get({
      endpoint: `/api/v1/donor_semens/me`,
      onSuccess: (res) => {
        setNewFiles([]);
        setDelFileIds([]);

        const persistedSemen = new DonorSemen(res.data);
        if (persistedSemen.expired) {
          setSemen(new DonorSemen());
        } else {
          setSemen(persistedSemen);
        }
      },
      onError: (err) => {
        toast.error("情報の取得に失敗しました。時間をおいてお試しください。");
      },
    });
  }, [ get ]);

  useEffect(() => {
    fetchSemenData();
  }, [ fetchSemenData ]);

  const handleSave = () => {
    if (semen?.status === 'SUBMITTED') {
      toast.error("申請中のため、編集できません。");
      return false;
    }

    if (semen?.status === 'CERTIFIED' && !semen.expired) {
      if (!window.confirm('保存すると再申請＆承認されるまで未提出扱いとなります。保存しますか？')){
        return;
      }
    }

    if (semen?.collectedOn === '') {
      toast.error("採取日を入力してください。");
      return false;
    }

    if (!semen?.donorSemenFiles || (semen.donorSemenFiles.length === 0 && newFiles.length === 0)) {
      toast.error("検査結果書類を1つ以上添付してください。");
      return false;
    }
    if ((semen?.donorSemenFiles?.length || 0) + newFiles.length > 5) {
      toast.error("最大5ファイルまで添付できます。");
      return false;
    }

    if (!semen?.in6Month) {
      toast.error("6ヶ月以内の検査結果のみ登録可能です。");
      return false;
    }

    if (!semen?.volume || !semen?.concentration || !semen?.totalCount) {
      toast.error("必須項目はすべて入力してください。");
      return false;
    }

    const formData = new FormData();
    formData.append('collected_on', semen.collectedOn);
    formData.append('volume', semen.volume.toString());
    formData.append('concentration', semen.concentration.toString());
    formData.append('total_count', semen.totalCount.toString());
    formData.append('motility', semen.motility ? semen.motility.toString() : "");
    formData.append('morphology', semen.morphology ? semen.morphology.toString() : "");
    formData.append('del_file_ids', delFileIds.join(','));
    for (const file of newFiles) {
      formData.append('files[]', file);
    }

    post({
      endpoint: '/api/v1/donor_semens/me',
      method: 'PATCH',
      formData: formData,
      onSuccess: () => {
        toast.success("保存しました。");
        navigate('/mypage/checkups');
      },
      onError: (err) => {
        toast.error("保存に失敗しました。時間をおいて再度お試しください。");
      },
    })
  }

  const handleBlur = async () => {
    const newSemen = semen ? await semen?.updateTotalFromApi() : null;
    setSemen(newSemen);
  }

  return (
    <main className="sub">
      { loading && <Spinner overlay={ true } /> }
      <section className="donor_semen">
        <h2 className="main_title"><span>精液検査結果</span></h2>
        <div className="form">
          {semen?.status === 'REMANDED' && (
            <div className="border_box pink">
              <span className="txt_pink alert_msg">
                {semen.comments && semen.comments.split('\n').map((line, idx) => (
                  <React.Fragment key={ idx }>
                    { line }<br />
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          <dl className="graf_list">
            <dt>採取日 *</dt>
            <dd>
              <input
                type="date"
                value={ semen?.collectedOn || '' }
                onChange={ e => setSemen(prev => prev ? prev.set("collectedOn", e.target.value) : null) }
              />
            </dd>
            <dt>精液量 *</dt>
            <dd>
              <input
                className="with_unit"
                type="number"
                step="0.2"
                value={ semen?.volume || '' }
                onWheel={ e => e.currentTarget.blur() }
                onChange={ e => {
                  const value = e.target.value;
                  const regex = /^\d*(\.\d{0,2})?$/;
                  if (regex.test(value)) {
                    setSemen(prev => prev ? prev.set("volume", Number(value)) : null);
                  }
                }}
                onBlur={ handleBlur }
              />ml
            </dd>
            <dt>精子濃度 *</dt>
            <dd>
              <input
                className="with_unit"
                type="number"
                step="0.2"
                value={ semen?.concentration || '' }
                onWheel={ e => e.currentTarget.blur() }
                onChange={ e => {
                  const value = e.target.value;
                  const regex = /^\d*(\.\d{0,2})?$/;
                  if (regex.test(value)) {
                    setSemen(prev => prev ? prev.set("concentration", Number(value)) : null);
                  }
                }}
                onBlur={ handleBlur }
              />万/ml
            </dd>
            <dt>総精子数 *</dt>
            <dd>
              <input
                className="with_unit"
                type="number"
                step="0.2"
                value={ semen?.totalCount || '' }
                onWheel={ e => e.currentTarget.blur() }
                onChange={ e => {
                  const value = e.target.value;
                  const regex = /^\d*(\.\d{0,2})?$/;
                  if (regex.test(value)) {
                    setSemen(prev => prev ? prev.set("totalCount", Number(value)) : null);
                  }
                }}
                onBlur={ handleBlur }
              />万
            </dd>
            <dt>運動率</dt>
            <dd>
              <input
                className="with_unit"
                type="number"
                step="0.2"
                value={ semen?.motility || '' }
                onWheel={ e => e.currentTarget.blur() }
                onChange={ async e => {
                  const value = e.target.value;
                  const regex = /^\d*(\.\d{0,2})?$/;
                  if (regex.test(value)) {
                    setSemen(prev => prev ? prev.set("motility", Number(value)) : null);
                  }
                }}
                onBlur={ handleBlur }
              />%
            </dd>
            <dt>正常形態率</dt>
            <dd>
              <input
                className="with_unit"
                type="number"
                value={ semen?.morphology || '' }
                onWheel={ e => e.currentTarget.blur() }
                onChange={ async e => {
                  const value = e.target.value;
                  console.log(value)
                  const regex = /^\d*(\.\d{0,2})?$/;
                  if (regex.test(value)) {
                    setSemen(prev => prev ? prev.set("morphology", Number(value)) : null);
                  }
                }}
                onBlur={ handleBlur }
              />%
            </dd>
          </dl>
          <div className="judge">
            総合判定
            <span className={ semen?.total ? 'org' : 'txt_pink' } >
              { semen?.total || "---" }
            </span>
          </div>
        </div>
        <h3 className="sub_title">検査結果書類をアップロードしてください</h3>
        <div className="filebtn">
          {(() => {
            // 保存済みファイル（DonorSemenFile）と新規ファイル（File）を結合し最大5個まで表示
            const filesToDisplay = [
              ...semen?.donorSemenFiles || [],
              ...newFiles
            ].slice(0, 5);
            const totalCount = filesToDisplay.length;
            return (
              <>
                {filesToDisplay.map((file, idx) => (
                  <div key={idx} className="files_wrap">
                    <span className="filename">
                      {file ? file.name : "ファイルが選択されていません"}
                    </span>
                    {('id' in file) ? (
                      // 保存済みファイル
                      <label>
                        <button
                          type="button"
                          className="fileinput"
                          onClick={() => {
                            // delFileIdsに追加し、donorSemenFilesから除外
                            setDelFileIds(ids => [...ids, (file as DonorSemenFile).id]);
                            setSemen(prev => prev ? prev.set(
                              "donorSemenFiles",
                              prev.donorSemenFiles.filter(f => (f as DonorSemenFile).id !== (file as DonorSemenFile).id)
                            ) : null);
                          }}
                        />削除
                      </label>
                    ) : (
                      // 新規ファイル
                      <label>
                        <button
                          type="button"
                          className="fileinput"
                          onClick={() => {
                            setNewFiles(prev => prev.filter(f => f.name !== (file as File).name));
                          }}
                        />削除
                      </label>
                    )}
                  </div>
                ))}
                {/* 空きスロットにファイル選択を表示 */}
                {[...Array(5 - totalCount)].map((_, idx) => (
                  <div key={totalCount + idx} className="files_wrap">
                    <span className="filename">ファイルが選択されていません</span>
                    <label>
                      <input
                        type="file"
                        name={`ファイル選択${totalCount + idx}`}
                        className="fileinput"
                        accept="image/*,application/pdf"
                        onChange={e => {
                          const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                          if (!file) return;
                          setNewFiles(prev => [...prev, file].slice(0, 5));
                        }}
                      />ファイル選択
                    </label>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
        <ul className="box">
          <li><h2>【注意事項】</h2></li>
          <li>＊検査結果書類は<strong>「検査機関」</strong>と<strong>「検査ID（報告書番号、キットIDなど）」</strong>がわかるものを提出してください。</li>
          <li>＊病院での検査結果の場合、<strong>「診察券」「診療明細」</strong>のコピーも提出してください。</li>
          <li>＊提出書類をもとに検査機関に問い合わせを行う場合があります。</li>
          <li>＊書類の信憑性を確認できない場合は登録をお断りします。書類の偽造など悪質な場合は、返金致しませんのでご了承ください。</li>
          <li>＊書類の<strong className="text-red">偽造/変造は犯罪</strong>です。刑法により処罰される可能性があります。</li>
          <li><h2>【入力項目と書類について】</h2></li>
          <li>＊検査結果書類はウェブ画面のスクリーンショットでも可能です。</li>
          <li>＊検査結果書類の画像を他ユーザーが閲覧することはありません。</li>
          <li>＊採取日の記載が書類にない場合は、検査日/受付日などの日付でも登録可能です。</li>
          <li>＊書類に記載のある項目は全て記入してください。記入漏れがある場合は、運営事務局にて書類を基に訂正を行います。</li>
          <li>＊正常形態率=（100% - 奇形率）になります。</li>
          <li>＊正常形態率に幅がある場合は最大値を記入してください。</li>
          <li>＊検査結果の有効期間は採取日から１年としております。</li>
          <li>＊6ヶ月以内の検査結果のみ登録可能です。</li>
          <li>＊個人情報の取り扱いについては<Link to="/privacy">プライバシーポリシー</Link>のページをご覧ください。</li>
        </ul>
        <ul className="btnarea">
          <li>
            <input
              type="submit"
              value={ semen?.status === 'REMANDED' ? "再提出する" : "保存する" }
              className="w_bluebtn"
              onClick={ handleSave }
              disabled={ loading }
            />
          </li>
          <li>
            <Link to="/mypage/checkups" className="w_bluebtn">
              戻る
            </Link>
          </li>
        </ul>
      </section>
    </main>
  );
}

export default MypageCheckupSemen;
